import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Badge, ErrorText } from "../components/ui/feedback";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { EmptyState } from "../components/ui/EmptyState";
import { Menu, type MenuItem } from "../components/ui/Menu";
import { PageHeader } from "../components/ui/PageHeader";
import { formatDateTime } from "../lib/time";
import { adminApi } from "./api";
import { inviteDisplayState, type InviteDisplayState } from "./invite-status";
import { DenseTable, QueryError, TD_CLASS, TH_CLASS } from "./dense";
import { InviteRequestsSection } from "./InviteRequestsSection";
import { ADMIN_KEY, adminInvitesQuery } from "./queries";
import type { AdminInvite } from "./types";

type Dialog = { kind: "newLink" | "revoke"; invite: AdminInvite } | null;

const STATE_TONE: Record<InviteDisplayState, "accent" | "red" | "muted"> = {
  pending: "accent",
  opened: "accent",
  used: "muted",
  expired: "muted",
  revoked: "red",
};

/** Sound-guy invites across all workspaces: status, expiry, and the row menu (copy, resend, new link, revoke). */
export function AdminInvitesPage() {
  const { t, lang } = useI18n();
  const queryClient = useQueryClient();
  const invites = useQuery(adminInvitesQuery);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [copyFailed, setCopyFailed] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: [...ADMIN_KEY, "invites"] });
  const resend = useMutation({
    mutationFn: (id: string) => adminApi.resendInvite(id),
    onSuccess: refresh,
  });
  const newLink = useMutation({
    mutationFn: (id: string) => adminApi.regenerateInvite(id),
    onSuccess: () => {
      setDialog(null);
      return refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => adminApi.revokeInvite(id),
    onSuccess: () => {
      setDialog(null);
      return refresh();
    },
  });
  const busy = resend.isPending || newLink.isPending || revoke.isPending;

  const confirmDialog = () => {
    if (dialog?.kind === "newLink") newLink.mutate(dialog.invite.id);
    if (dialog?.kind === "revoke") revoke.mutate(dialog.invite.id);
  };

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url).then(
      () => setCopyFailed(false),
      () => setCopyFailed(true),
    );
  };

  const rows = invites.data ?? [];

  const menuFor = (invite: AdminInvite): MenuItem[] => {
    const state = inviteDisplayState(invite);
    const canResend = state === "pending" || state === "opened";
    const canNewLink = invite.status === "pending" || invite.status === "expired";
    const items: (MenuItem | null)[] = [
      invite.url
        ? { label: t("admin.invites.copyLink"), onSelect: () => copyLink(invite.url ?? "") }
        : null,
      canResend
        ? {
            label: t("admin.invites.resend"),
            disabled: busy,
            onSelect: () => resend.mutate(invite.id),
          }
        : null,
      canNewLink
        ? {
            label: t("admin.invites.newLink"),
            disabled: busy,
            onSelect: () => setDialog({ kind: "newLink", invite }),
          }
        : null,
      canNewLink
        ? {
            label: t("admin.invites.revoke"),
            tone: "danger",
            disabled: busy,
            onSelect: () => setDialog({ kind: "revoke", invite }),
          }
        : null,
    ];
    return items.filter((item): item is MenuItem => item !== null);
  };

  return (
    <div className="space-y-5">
      <PageHeader title={t("admin.invites.title")} />
      <InviteRequestsSection />
      <ErrorText error={resend.error ?? newLink.error ?? revoke.error} />
      {copyFailed ? <ErrorText>{t("link.shareUnsupported")}</ErrorText> : null}
      {invites.isError ? (
        <QueryError error={invites.error} onRetry={() => void invites.refetch()} />
      ) : null}
      {invites.isPending ? <p className="text-operate text-ink-3">{t("app.loading")}</p> : null}
      {invites.data && rows.length === 0 ? (
        <EmptyState>{t("admin.invites.empty")}</EmptyState>
      ) : null}

      {rows.length > 0 ? (
        <DenseTable label={t("admin.invites.title")}>
          <thead>
            <tr>
              <th scope="col" className={TH_CLASS}>
                {t("admin.col.email")}
              </th>
              <th scope="col" className={`${TH_CLASS} hidden md:table-cell`}>
                {t("admin.col.workspace")}
              </th>
              <th scope="col" className={TH_CLASS}>
                {t("admin.col.status")}
              </th>
              <th scope="col" className={`${TH_CLASS} hidden md:table-cell`}>
                {t("admin.invites.col.expires")}
              </th>
              <th scope="col" className={`${TH_CLASS} text-right`}>
                <span className="sr-only">{t("admin.col.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((invite) => {
              const state = inviteDisplayState(invite);
              return (
                <tr key={invite.id} className="hover:bg-blue-wash/40">
                  <td className={`${TD_CLASS} break-all`}>{invite.email}</td>
                  <td className={`${TD_CLASS} hidden text-ink-2 md:table-cell`}>
                    {invite.workspaceName ?? t("common.none")}
                  </td>
                  <td className={TD_CLASS}>
                    <Badge tone={STATE_TONE[state]}>{t(`admin.invites.status.${state}`)}</Badge>
                  </td>
                  <td className={`${TD_CLASS} hidden tabular-nums text-ink-2 md:table-cell`}>
                    {formatDateTime(invite.expiresAt, lang)}
                  </td>
                  <td className={`${TD_CLASS} text-right`}>
                    <Menu
                      label={t("roster.moreActions", { name: invite.email })}
                      items={menuFor(invite)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </DenseTable>
      ) : null}

      {dialog?.kind === "newLink" ? (
        <ConfirmDialog
          title={t("admin.invites.newLinkTitle")}
          message={t("admin.invites.newLinkMessage", { email: dialog.invite.email })}
          confirmLabel={t("admin.invites.newLink")}
          busy={newLink.isPending}
          error={newLink.error}
          onConfirm={confirmDialog}
          onCancel={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "revoke" ? (
        <ConfirmDialog
          title={t("admin.invites.revokeTitle")}
          message={t("admin.invites.revokeMessage", { email: dialog.invite.email })}
          confirmLabel={t("admin.invites.revoke")}
          variant="danger"
          busy={revoke.isPending}
          error={revoke.error}
          onConfirm={confirmDialog}
          onCancel={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
